import type { DocEntityId } from "building-types/doc/docEntityId";

/**
 * Separator used in derived instance option set IDs.
 * Chosen because colons are not used in existing entity ID prefixes.
 */
const DERIVED_ID_SEPARATOR = ":";

/**
 * Derives an instance option set ID from its archetype option set ID and component instance ID.
 *
 * Format: `{archetypeOptionSetId}:{componentInstanceId}`
 * Example: `optionSet_abc123:componentInstance_xyz789`
 *
 * This derived format is predictable (can be computed without storing the entity)
 * and parseable (can extract both IDs by splitting on the separator).
 *
 * UID → derived migration: `migration_20260327_derived_instance_option_set_ids.ts`.
 */
export function deriveInstanceOptionSetId(
  archetypeOptionSetId: string,
  componentInstanceId: string,
): DocEntityId<"INSTANCE_OPTION_SET"> {
  return `${archetypeOptionSetId}${DERIVED_ID_SEPARATOR}${componentInstanceId}` as DocEntityId<"INSTANCE_OPTION_SET">;
}

/**
 * Parses a derived instance option set ID to extract the archetype option set ID
 * and component instance ID.
 *
 * Returns null if the ID is not in the expected derived format (e.g., legacy UID-based IDs).
 */
export function parseInstanceOptionSetId(id: string): { archetypeId: string; componentInstanceId: string } | null {
  const parts = id.split(DERIVED_ID_SEPARATOR);
  if (parts.length !== 2) {
    return null;
  }

  return {
    archetypeId: parts[0],
    componentInstanceId: parts[1],
  };
}

/**
 * Returns the archetype option-set id for a derived instance id (`archetypeId:instanceId`),
 * or the id itself for regular/archetype option sets.
 * See getOptionSetArchetypeOrSelf for looking up actual option set objects.
 */
export function getOptionSetArchetypeIdOrSelf(id: string): string {
  return parseInstanceOptionSetId(id)?.archetypeId ?? id;
}
